import { useState, useEffect } from "react";
import { LanguageProvider } from "./LanguageContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomeView from "./views/HomeView";
import ServicesView from "./views/ServicesView";
import ServiceDetailView from "./views/ServiceDetailView";
import ProjectsView from "./views/ProjectsView";
import ProjectDetailView from "./views/ProjectDetailView";
import AboutView from "./views/AboutView";
import ContactView from "./views/ContactView";
import PrivacyPolicyView from "./views/PrivacyPolicyView";
import TermsOfServiceView from "./views/TermsOfServiceView";

export default function App() {
  const [currentView, setCurrentView] = useState("home");
  const [selectedKey, setSelectedKey] = useState(null);

  // Disable browser automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Handle routing via hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      const path = hash.startsWith("/") ? hash.substring(1) : hash;
      
      if (!path) {
        setCurrentView("home");
        setSelectedKey(null);
      } else {
        const [view, key] = path.split("/");
        setCurrentView(view || "home");
        setSelectedKey(key || null);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // Ensure page starts at top on every view change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView, selectedKey]);

  const navigate = (view, key = null) => {
    const newHash = key ? `/${view}/${key}` : `/${view}`;
    
    // If we're already on the same hash, manually trigger scroll since hash won't change
    if (window.location.hash === `#${newHash}`) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.hash = newHash;
    }
  };

  const renderView = () => {
    const ViewComponent = {
      home: HomeView,
      services: ServicesView,
      "service-detail": ServiceDetailView,
      projects: ProjectsView,
      "project-detail": ProjectDetailView,
      about: AboutView,
      contact: ContactView,
      "privacy-policy": PrivacyPolicyView,
      "terms-of-service": TermsOfServiceView,
    }[currentView] || HomeView;

    return <ViewComponent navigate={navigate} selectedKey={selectedKey} />;
  };

  return (
    <LanguageProvider>
      <div className="flex flex-col min-h-screen">
        <Navbar navigate={navigate} currentView={currentView} />
        <main className="flex-grow">{renderView()}</main>
        <Footer navigate={navigate} />
      </div>
    </LanguageProvider>
  );
}
